import { RefreshCw } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface AudioShimmerProps {
  status: "pending" | "failed"
  onRetry?: () => void
  variant?: "compact" | "full"
  className?: string
}

/**
 * Animated shimmer component shown while flashcard audio is generating.
 * Displays animated sound wave bars with a gradient sweep effect.
 *
 * - `pending`: Shows animated waveform with "Generating audio..." text
 * - `failed`: Shows error state with optional retry button
 */
export function AudioShimmer({
  status,
  onRetry,
  variant = "compact",
  className,
}: AudioShimmerProps) {
  if (status === "failed") {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
          className,
        )}
      >
        <span className="text-xs text-red-600 dark:text-red-400">
          Audio generation failed
        </span>
        {onRetry && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRetry}
            className="h-6 px-2 text-xs text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/40"
          >
            <RefreshCw className="mr-1 size-3" />
            Retry
          </Button>
        )}
      </div>
    )
  }

  // Pending state - animated sound wave shimmer
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-lg",
        variant === "full"
          ? "bg-gradient-to-r from-violet-50 via-fuchsia-50 to-violet-50 px-4 py-3 dark:from-violet-950/30 dark:via-fuchsia-950/30 dark:to-violet-950/30"
          : "bg-slate-100 px-3 py-2 dark:bg-slate-800",
        className,
      )}
    >
      {/* Shimmer sweep overlay */}
      <div
        className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.1) 50%, transparent 100%)",
        }}
      />

      <div className="relative flex items-center gap-3">
        {/* Animated sound wave bars */}
        <div className="flex items-center gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={cn(
                "w-1 rounded-full bg-gradient-to-t from-violet-400 to-fuchsia-400 dark:from-violet-500 dark:to-fuchsia-500",
                "animate-[wave_1s_ease-in-out_infinite]",
              )}
              style={{
                height: variant === "full" ? "16px" : "12px",
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>

        {/* Text */}
        <span
          className={cn(
            "font-medium",
            variant === "full"
              ? "text-sm text-violet-700 dark:text-violet-300"
              : "text-xs text-slate-600 dark:text-slate-400",
          )}
        >
          Generating audio...
        </span>
      </div>

      {/* Inline keyframes via style tag */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
        @keyframes wave {
          0%, 100% {
            transform: scaleY(0.4);
            opacity: 0.6;
          }
          50% {
            transform: scaleY(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}
